// ShippingAddressModal.js

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./ShippingAddressModal.module.css";
import Modal from "components/Modal";
import Button from "Shared/Button";
import AddressSettingsList from "components/AddressSettingsList";
import AddressModal from "components/AddressModal";
import ValidateAddressModal from "components/ValidateAddressModal";
import { useTranslation } from "react-i18next";
import {
  useAddresses,
  useUpdateAddress,
  useCreateAddress,
  useValidateAddress,
} from "../../hooks/useAddresses";
import { useApiErrorMessage } from "../../hooks/useApiErrorMessage";

const getAddressId = (address) =>
  address?.addressId ?? address?.iboAddressId ?? address?.id;

const isAddressPrimary = (address) =>
  Boolean(address?.isPrimary) || Number(address?.primary ?? 0) === 1;

const isAddressDefault = (address) =>
  Boolean(address?.isDefault) || Number(address?.shipping ?? 0) === 1;

const normalizeAddress = (address, index = 0) => ({
  ...address,
  id: address.id ?? address.addressId ?? address.iboAddressId ?? index,
  addressId: address.addressId ?? address.iboAddressId ?? address.id ?? index,
  address1: address.address1 ?? address.line1 ?? address.street ?? "",
  address2: address.address2 ?? address.line2 ?? address.street2 ?? "",
  zip: address.zip ?? address.zipCode ?? address.postalCode ?? "",
});

export function ShippingAddressModal({
  isOpen,
  onClose,
  onSave,
  onCancel,
  onAdd,
}) {
  const { t } = useTranslation();
  const apiErrorMessage = useApiErrorMessage();
  // Only fetch once the modal is actually visible; CartPage's own
  // useAddresses() call keeps the summary address populated regardless.
  const {
    data: apiAddresses = [],
    isSuccess: addressesLoaded,
    refetch: refetchAddresses,
  } = useAddresses({ enabled: isOpen });
  const updateAddress = useUpdateAddress();
  const createAddress = useCreateAddress();
  const validateAddress = useValidateAddress();
  const [addressList, setAddressList] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const initialPrimaryIdRef = useRef(null);
  const initialDefaultIdRef = useRef(null);
  // Guards the seed-from-API effect below so it only runs once per modal
  // opening: the query is gated behind isOpen now, so on a cold cache the
  // effect must wait for the fetch to resolve rather than seeding from an
  // empty apiAddresses on the same render isOpen flips true.
  const hasSeededRef = useRef(false);

  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [showValidateAddModal, setShowValidateAddModal] = useState(false);
  const [newAddressDraft, setNewAddressDraft] = useState(null);
  const [suggestedNewAddress, setSuggestedNewAddress] = useState(null);
  const [addAddressError, setAddAddressError] = useState("");
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      // AddressSettingsList unmounts with the modal and re-seeds its default
      // selection from whatever addressList holds on mount. Clearing the list
      // here stops it from seeding off the previous session's stale flags
      // before this effect re-seeds from the refreshed API data.
      hasSeededRef.current = false;
      setAddressList([]);
      setSelectedAddress(null);
      return;
    }
    if (hasSeededRef.current || !addressesLoaded) return;
    hasSeededRef.current = true;

    const normalizedAddresses = apiAddresses.map(normalizeAddress);

    setAddressList(normalizedAddresses);
    setSaveError("");
    initialPrimaryIdRef.current = getAddressId(
      normalizedAddresses.find(isAddressPrimary) ?? null
    );
    // Initialize default from the address with shipping: 1, not from primary
    initialDefaultIdRef.current = getAddressId(
      normalizedAddresses.find(isAddressDefault) ?? null
    );
    // Always select the current default address on modal open
    const defaultAddress = normalizedAddresses.find(isAddressDefault);
    setSelectedAddress(defaultAddress ?? normalizedAddresses[0] ?? null);
  }, [isOpen, addressesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleSave = async () => {
    setSaveError("");

    const newPrimaryAddress = addressList.find(isAddressPrimary) ?? null;
    const newPrimaryId = getAddressId(newPrimaryAddress);
    const primaryChanged =
      newPrimaryAddress &&
      String(newPrimaryId) !== String(initialPrimaryIdRef.current);

    // The "default shipping address" checkbox is tracked separately from the
    // primary toggle (see AddressSettingsList's onPrimaryChange), so it needs
    // its own PUT even when the primary address hasn't changed.
    const newDefaultAddress = selectedAddress ?? addressList[0] ?? null;
    const newDefaultId = getAddressId(newDefaultAddress);
    const defaultChanged =
      newDefaultAddress &&
      String(newDefaultId) !== String(initialDefaultIdRef.current);

    if (!primaryChanged && !defaultChanged) {
      onSave?.(selectedAddress ?? addressList[0] ?? null);
      onClose?.();
      return;
    }

    setIsSaving(true);
    try {
      if (primaryChanged) {
        await updateAddress.mutateAsync({
          ...newPrimaryAddress,
          addressId: newPrimaryId,
          primary: 1,
          isPrimary: true,
          bitwiseType: 1,
        });
      }

      if (
        defaultChanged &&
        (!primaryChanged || String(newDefaultId) !== String(newPrimaryId))
      ) {
        await updateAddress.mutateAsync({
          ...newDefaultAddress,
          addressId: newDefaultId,
          shipping: 1,
        });
      }

      onSave?.(selectedAddress ?? addressList[0] ?? null);
      onClose?.();
    } catch (err) {
      setSaveError(
        apiErrorMessage(
          err,
          t(
            "errorModal.address",
            "We could not update your primary address. Please try again."
          )
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  const handleOpenAddAddress = () => {
    setNewAddressDraft(null);
    setAddAddressError("");
    setValidationError("");
    setShowAddAddressModal(true);
  };

  const handleAddAddressValidate = (addressData) => {
    setNewAddressDraft(addressData);
    setValidationError("");
    validateAddress.mutate(addressData, {
      onSuccess: (suggested) => {
        setSuggestedNewAddress(suggested ?? addressData);
        setShowAddAddressModal(false);
        setShowValidateAddModal(true);
      },
      onError: (err) => {
        setValidationError(
          apiErrorMessage(
            err,
            t(
              "errorModal.validateAddress",
              "We could not validate your address. Please check it and try again."
            )
          )
        );
      },
    });
  };

  const finalizeNewAddress = async (addressToCreate) => {
    if (!addressToCreate) return;
    setShowValidateAddModal(false);
    setAddAddressError("");

    const existingIds = new Set(
      addressList.map((address) => String(getAddressId(address)))
    );

    try {
      await createAddress.mutateAsync(addressToCreate);

      // The create endpoint may return an empty body, so refetch to get the
      // server-assigned addressId for the new record instead of guessing one.
      const { data: refreshedAddresses = [] } = await refetchAddresses();
      const normalizedRefreshed = refreshedAddresses.map(normalizeAddress);
      const createdAddress =
        normalizedRefreshed.find(
          (address) => !existingIds.has(String(getAddressId(address)))
        ) ?? normalizedRefreshed[normalizedRefreshed.length - 1];

      if (!createdAddress) {
        setAddAddressError(
          t(
            "errorModal.address",
            "We could not add your address. Please try again."
          )
        );
        return;
      }

      setAddressList(normalizedRefreshed);
      onAdd?.(createdAddress);
    } catch (err) {
      setAddAddressError(
        apiErrorMessage(
          err,
          t(
            "errorModal.address",
            "We could not add your address. Please try again."
          )
        )
      );
    }
  };

  const handleAddModalClose = () => {
    setValidationError("");
    setShowAddAddressModal(false);
  };

  const handleValidateAddModalClose = () => {
    setShowValidateAddModal(false);
  };

  const handleEditNewAddress = () => {
    setShowValidateAddModal(false);
    setShowAddAddressModal(true);
  };

  return (
    <Modal isOpen={isOpen}>
      <div className={styles.container}>
        <div className={styles.scrollArea}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.addButton}>
              <Button
                onClick={handleOpenAddAddress}
                text={t("Add Address")}
                className="closeBtn"
              />
            </div>
          </div>

          {/* Address list */}
          <AddressSettingsList
            addressList={addressList}
            onAddressUpdate={(updated) => setAddressList(updated)}
            onPrimaryChange={setSelectedAddress}
          />

          {addAddressError && (
            <div className={styles.editError}>{addAddressError}</div>
          )}
          {saveError && <div className={styles.editError}>{saveError}</div>}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button
            text={t("Cancel")}
            onClick={handleCancel}
            className="closeBtn"
            disabled={isSaving}
          />
          <Button
            text={t("Save")}
            onClick={handleSave}
            className="confirmBtn"
            disabled={isSaving}
          />
        </div>
      </div>

      <AddressModal
        address={newAddressDraft}
        isOpen={showAddAddressModal}
        onClose={handleAddModalClose}
        onValidate={handleAddAddressValidate}
        errorMessage={validationError}
        title={t("Add Address")}
      />

      <ValidateAddressModal
        addressInfo={newAddressDraft}
        suggestedAddress={suggestedNewAddress}
        isOpen={showValidateAddModal}
        onClose={handleValidateAddModalClose}
        onUseSuggested={() => finalizeNewAddress(suggestedNewAddress)}
        onUseOriginal={() => finalizeNewAddress(newAddressDraft)}
        onEditOriginal={handleEditNewAddress}
      />
    </Modal>
  );
}

ShippingAddressModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onAdd: PropTypes.func,
};

export default ShippingAddressModal;
